// AccesoCorreoSection.js — Sección `acceso_correo`: tarjeta oscura con título,
// texto, imagen y el formulario de ingreso por correo (Formación regulatoria de LAAC).
//
// `modo`:
//   · 'contrasena' (por defecto): se escribe el correo y luego la contraseña.
//   · 'sin_contrasena': basta el correo. Solo funciona si el proyecto tiene
//     «Ingreso sin contraseña» activado (registro_config.login_sin_password).
//   · 'externo': lleva a `url_externa` con el correo en {email} (otra plataforma).
//   · 'sso': el correo se manda al servidor SSO de la otra plataforma (el mismo
//     protocolo del plugin «SSO Gateway Client»: firma HMAC + Turnstile) y se
//     redirige a la dirección de un solo uso que responde. Se configura en la
//     ficha del proyecto → Acceso SSO.
// Al entrar va a `url_destino`. Con la sesión ya abierta muestra un botón directo.
import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { authService, ssoGatewayService, BACKEND_BASE } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useProject } from '../../../contexts/ProjectContext';
import Turnstile, { useTurnstile } from './Turnstile';
import CaptchaCheck from './CaptchaCheck';
import { abrirSolicitudAcceso } from './SolicitudAccesoSection';
import VerContrasena from '../../VerContrasena';

const urlImagen = (v) => (!v ? null : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));

const Seccion = styled.section`
  background: ${p => p.$fondo};
  padding: ${p => p.$padding};
  @media (max-width: 767px) { padding: 40px 0; }
`;

const Tarjeta = styled.div`
  width: 90%;
  max-width: ${p => p.$ancho};
  margin: 0 auto;
  box-sizing: border-box;
  border-radius: 23px;
  padding: 40px 40px 100px;
  background-image: ${p => p.$fondo};
  font-family: Arial, Helvetica, sans-serif;
  text-align: left;
  h2 { margin: 0 0 16px; padding: 0; font-family: 'Roboto', Helvetica, Arial, sans-serif; font-size: 22px; font-weight: 700; line-height: 1.2; color: #fff; }
  .texto { color: ${p => p.$colorTexto}; font-size: 16px; line-height: 1.3em; margin: 0 0 20px; }
  .imagen { display: block; width: 100%; max-width: 500px; height: auto; margin: 0 auto 30px; border-radius: 10px; }
  @media (max-width: 767px) { padding: 28px 20px 40px; h2 { font-size: 20px; } }
`;

const Form = styled.form`
  max-width: 300px;
  margin: 2.6em auto 0;
  display: flex;
  flex-direction: column;
  gap: 1.1em;
  color: #f3f4f6;
  label { display: flex; flex-direction: column; gap: 0.45em; font-weight: 600; font-size: 17px; text-align: center; letter-spacing: .2px; }
  label .req { color: #f0abfc; margin-left: 4px; }
  input {
    width: 100%; box-sizing: border-box; padding: 9px 15px; font-size: 16px; line-height: 1.5; font-family: inherit;
    border: 1px solid rgba(255,255,255,.14); border-radius: 6px; background: #fff; color: #000; outline: none;
  }
  input[aria-invalid='true'] { border-color: rgba(248,113,113,.85); }
  .estado { min-height: 1.4em; font-size: 15px; text-align: center; color: #e5e7eb; }
  .estado.error { color: #fca5a5; }
  .olvido { text-align: center; font-size: 14px; }
  /* Correo sin cuenta en el proyecto (modo SSO con «solo registrados»): aviso, no error. */
  .sin-autorizacion {
    display: flex; flex-direction: column; align-items: center; gap: 0.8em;
    padding: 1em 1.1em; border-radius: 10px; text-align: center;
    background: rgba(255,255,255,.08); border: 1px solid rgba(240,171,252,.45);
    color: #f5f3ff; font-size: 15px; line-height: 1.45;
  }
  .sin-autorizacion strong { display: block; font-size: 16px; color: #fff; margin-bottom: 0.2em; }
  .sin-autorizacion button {
    border: 1px solid #f0abfc; background: transparent; color: #fff; border-radius: 9px;
    padding: 8px 18px; font: inherit; font-weight: 600; cursor: pointer; transition: background .2s ease;
  }
  .sin-autorizacion button:hover { background: rgba(240,171,252,.18); }
  .olvido a { color: #ddcafc; }
  .olvido a:hover { color: #fff; }
`;

const Boton = styled.button`
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px 8px;
  border: none;
  border-radius: 11px;
  background: linear-gradient(90deg, #c026d3 30%, #9333ea 70%, #7e22ce 100%);
  color: #fff;
  font-weight: 600;
  font-family: inherit;
  letter-spacing: .5px;
  font-size: 16px;
  cursor: pointer;
  transition: all .3s ease;
  text-decoration: none;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 22px rgb(168 85 247 / .35); }
  &:disabled { opacity: .7; cursor: progress; }
`;

const AccesoCorreoSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const modo = c.modo || 'contrasena';
  const destino = c.url_destino || '/contenidos';
  const { user, login } = useAuth();
  const { proyecto } = useProject();
  const navigate = useNavigate();
  // Turnstile del modo SSO: la clave pública la publica el proyecto.
  const siteKey = modo === 'sso' ? (proyecto?.config_json?.sso_turnstile_site_key || c.turnstile_site_key || '') : '';
  const { token: tokenTurnstile, setToken: setTokenTurnstile, reinicio, reiniciar: reiniciarTurnstile } = useTurnstile();
  const [trampa, setTrampa] = useState('');
  // Sin claves de Turnstile se usa el captcha propio (una operación sencilla).
  const [captcha, setCaptcha] = useState({ token: '', respuesta: '' });
  const [reinicioCaptcha, setReinicioCaptcha] = useState(0);
  // Si Cloudflare no puede pintar su widget (dominio no permitido, bloqueo de red…),
  // el formulario sigue sirviendo con la casilla propia.
  const [falloTurnstile, setFalloTurnstile] = useState(false);
  const conTurnstile = !!siteKey && !falloTurnstile;

  // En el modo SSO el ingreso es a OTRA plataforma: el correo se pide siempre,
  // aunque la persona ya tenga sesión aquí (se propone el suyo para no reescribirlo).
  const pedirSiempre = modo === 'sso';
  const [email, setEmail] = useState(() => (pedirSiempre && c.prellenar_email !== false ? (user?.email || '') : ''));
  const [password, setPassword] = useState('');
  const [pidePassword, setPidePassword] = useState(false);
  const [error, setError] = useState('');
  // Mensaje del servidor cuando el correo no tiene cuenta en el proyecto (modo SSO).
  const [noRegistrado, setNoRegistrado] = useState('');
  const [enviando, setEnviando] = useState(false);

  const irDestino = () => {
    if (/^https?:\/\//i.test(destino)) window.location.href = destino;
    else navigate(destino);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNoRegistrado('');
    const correo = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) {
      setError(tl(c.texto_email_invalido, 'Introduce un email válido.'));
      return;
    }
    if (modo === 'externo') {
      if (c.url_externa) window.location.href = c.url_externa.replace('{email}', encodeURIComponent(correo));
      return;
    }
    // SSO hacia otra plataforma: el servidor firma la petición y devuelve la
    // dirección de entrada (nunca se manda el correo por la URL).
    if (modo === 'sso') {
      if (conTurnstile && !tokenTurnstile) {
        setError(tl(c.texto_turnstile, 'Completa la verificación antes de continuar.'));
        return;
      }
      if (!conTurnstile && !captcha.respuesta) {
        setError(tl(c.texto_captcha, 'Confirma que no eres un robot para continuar.'));
        return;
      }
      setEnviando(true);
      try {
        const { data } = await ssoGatewayService.solicitar({
          proyecto_id: proyecto?.id,
          email: correo,
          cf_turnstile_response: tokenTurnstile || '',
          captcha_token: captcha.token,
          captcha_respuesta: captcha.respuesta,
          sitio_web: trampa,
        });
        if (data?.redirect_url) { window.location.assign(data.redirect_url); return; }
        setError(tl(c.texto_error, 'No pudimos completar el ingreso. Inténtalo de nuevo.'));
      } catch (err) {
        if (err.response?.data?.code === 'no_registrado') setNoRegistrado(err.response.data.message);
        else setError(err.response?.data?.message || tl(c.texto_error, 'No pudimos completar el ingreso. Inténtalo de nuevo.'));
        reiniciarTurnstile();
        setReinicioCaptcha(n => n + 1);
      } finally {
        setEnviando(false);
      }
      return;
    }
    if (modo === 'contrasena' && !pidePassword) {
      setPidePassword(true);
      return;
    }
    if (modo === 'contrasena' && !password) {
      setError(tl(c.texto_falta_password, 'Escribe tu contraseña.'));
      return;
    }
    setEnviando(true);
    try {
      const { data } = await authService.login(
        modo === 'sin_contrasena' ? { email: correo, passwordless: true } : { email: correo, password }
      );
      sessionStorage.setItem('auth_sesion_viva', '1');
      login({ token: data.token, user: data.user });
      irDestino();
    } catch (err) {
      setError(tl(c.texto_error, modo === 'sin_contrasena'
        ? 'No encontramos ese correo. Si el problema persiste, contacta con soporte.'
        : 'El correo o la contraseña no son correctos.'));
    } finally {
      setEnviando(false);
    }
  };

  const imagen = urlImagen(c.imagen);
  const textoBoton = tl(c.texto_boton, 'Acceder');

  return (
    <Seccion $fondo={c.color_fondo || '#f8f5ff'} $padding={c.padding || '60px 0'}>
      <Tarjeta
        $ancho={c.ancho_maximo || '700px'}
        $fondo={c.fondo_tarjeta || 'linear-gradient(180deg, #1a0a2e 0%, #2d1054 99%)'}
        $colorTexto={c.color_texto || '#cbc3d8'}
      >
        {tl(c.titulo, '') && <h2>{tl(c.titulo, '')}</h2>}
        {tl(c.texto, '') && <div className="texto" dangerouslySetInnerHTML={{ __html: tl(c.texto, '') }} />}
        {imagen && <img className="imagen" src={imagen} alt={tl(c.imagen_alt, '')} />}

        {user && !pedirSiempre ? (
          <Form as="div">
            <div className="estado">{tl(c.texto_con_sesion, 'Ya ingresaste como')} {user.nombre || user.email}</div>
            {/^https?:\/\//i.test(destino)
              ? <Boton as="a" href={destino}>{tl(c.texto_boton_sesion, 'Ir a mis cursos')}</Boton>
              : <Boton as={Link} to={destino}>{tl(c.texto_boton_sesion, 'Ir a mis cursos')}</Boton>}
          </Form>
        ) : (
          <Form onSubmit={onSubmit} noValidate>
            <label>
              <span>{tl(c.etiqueta_email, 'Email')}<span className="req" aria-hidden="true">*</span></span>
              <input type="email" value={email} autoComplete="email" inputMode="email" autoCapitalize="off" spellCheck="false"
                aria-invalid={error && !pidePassword ? 'true' : 'false'}
                onChange={e => { setEmail(e.target.value); if (pidePassword) setPidePassword(false); }} required />
            </label>
            {modo === 'contrasena' && pidePassword && (
              <label>
                <span>{tl(c.etiqueta_password, 'Contraseña')}<span className="req" aria-hidden="true">*</span></span>
                <VerContrasena flotante color={c.color_texto}>
                  {tipo => (
                    <input type={tipo} value={password} autoComplete="current-password" autoFocus
                      onChange={e => setPassword(e.target.value)} />
                  )}
                </VerContrasena>
              </label>
            )}
            {modo === 'sso' && (conTurnstile
              ? <Turnstile siteKey={siteKey} onToken={setTokenTurnstile} reinicio={reinicio}
                  onError={() => setFalloTurnstile(true)} />
              : <CaptchaCheck onCambio={setCaptcha} reinicio={reinicioCaptcha}
                  color={c.color_texto || '#ddcafc'} etiqueta={tl(c.etiqueta_captcha, 'No soy un robot')} />)}
            {modo === 'sso' && (
              <div style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
                <label htmlFor="sso_sitio_web">Sitio web</label>
                <input id="sso_sitio_web" tabIndex={-1} autoComplete="off" value={trampa} onChange={e => setTrampa(e.target.value)} />
              </div>
            )}
            <Boton type="submit" disabled={enviando} aria-busy={enviando}>
              {enviando ? 'Procesando…' : textoBoton}
            </Boton>
            {noRegistrado ? (
              <div className="sin-autorizacion" role="status" aria-live="polite">
                <div>
                  <strong>{tl(c.titulo_no_registrado, 'No tienes autorización todavía')}</strong>
                  {noRegistrado}
                </div>
                {c.mostrar_solicitar_no_registrado !== false && (
                  <button type="button" onClick={() => abrirSolicitudAcceso()}>
                    {tl(c.texto_boton_no_registrado, 'Solicitar acceso')}
                  </button>
                )}
              </div>
            ) : (
              <div className={`estado${error ? ' error' : ''}`} role="status" aria-live="polite">{error}</div>
            )}
            {modo === 'contrasena' && pidePassword && c.ocultar_olvido !== true && (
              <div className="olvido"><Link to={c.url_olvido || '/recuperar-contrasena'}>{tl(c.texto_olvido, '¿Olvidaste tu contraseña?')}</Link></div>
            )}
          </Form>
        )}
      </Tarjeta>
    </Seccion>
  );
};

export default AccesoCorreoSection;
